/**
 * Row actions menu for a Recurring templates table row.
 *
 * Desktop: dropdown anchored to the three-dot button. Opens downward
 * by default; flips upward when the row is near the bottom of the
 * viewport and the menu would overflow (fixes the last-row clipping
 * where only Edit + Duplicate were visible).
 *
 * Mobile: bottom sheet that slides up from the bottom with the
 * template name as a title.
 *
 * Rendered as a React Portal into document.body so it floats
 * above the sticky header, the side panel, and any z-index
 * stacking context from the table.
 *
 * Seven actions:
 *   - Edit template         (opens the builder drawer in edit mode)
 *   - Duplicate             (opens the builder drawer in create mode, pre-filled)
 *   - Run now               (opens ConfirmModal via onRunNow)
 *   - Skip next occurrence  (advances nextDate immediately)
 *   - Pause / Resume        (flips status via onTogglePause)
 *   - End this recurring    (sets status=ended, nextDate=null)
 *   - Delete template       (red, opens ConfirmModal via onDelete)
 *
 * Actions disable themselves when not applicable:
 *   - Skip next: disabled if nextDate is null
 *   - Run now: disabled if status is "ended"
 *   - End this recurring: disabled if status is already "ended"
 *
 * Keyboard: Esc closes, E = Edit, D = Duplicate.
 * Closes on outside click or route change.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import {
  Edit2,
  Copy,
  Play,
  SkipForward,
  Pause,
  Square,
  Trash2,
} from "lucide-react";
import type { RecurringTemplate } from "../recurringTypes";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
  red: "#B42318",
  redWash: "#FEF3F2",
};

const MENU_WIDTH = 240;
const MENU_GAP = 6;
const EDGE_MARGIN = 8;
const ESTIMATED_MENU_HEIGHT = 300;

interface RowActionsMenuProps {
  isOpen: boolean;
  template: RecurringTemplate;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onEdit: (t: RecurringTemplate) => void;
  onDuplicate: (t: RecurringTemplate) => void;
  onRunNow: (t: RecurringTemplate) => void;
  onSkipNext: (t: RecurringTemplate) => void;
  onTogglePause: (t: RecurringTemplate) => void;
  onEnd: (t: RecurringTemplate) => void;
  onDelete: (t: RecurringTemplate) => void;
}

export function RowActionsMenu({
  isOpen,
  template,
  anchorEl,
  onClose,
  onEdit,
  onDuplicate,
  onRunNow,
  onSkipNext,
  onTogglePause,
  onEnd,
  onDelete,
}: RowActionsMenuProps) {
  const { isMobile } = useBreakpoint();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Compute desktop dropdown position from the anchor's bounding rect.
  // Flips upward when there isn't enough space below (last-row case).
  useLayoutEffect(() => {
    if (!isOpen || isMobile || !anchorEl) {
      setPosition(null);
      return;
    }

    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      // Prefer the measured menu height once the menu is in the DOM.
      // On first pass, menuRef.current is null, so use the estimate;
      // the rAF call below re-runs this with the real measurement.
      const menuHeight = menuRef.current?.offsetHeight ?? ESTIMATED_MENU_HEIGHT;

      // Horizontal: right-align the menu to the anchor, clamp to viewport.
      let left = rect.right - MENU_WIDTH;
      if (left < EDGE_MARGIN) left = EDGE_MARGIN;
      const right = left + MENU_WIDTH;
      if (right > viewportW - EDGE_MARGIN) {
        left = viewportW - MENU_WIDTH - EDGE_MARGIN;
      }

      // Vertical: open below the anchor if it fits; otherwise flip above.
      const spaceBelow = viewportH - rect.bottom - EDGE_MARGIN;
      const spaceAbove = rect.top - EDGE_MARGIN;
      const needed = menuHeight + MENU_GAP;

      let top: number;
      if (needed <= spaceBelow) {
        top = rect.bottom + MENU_GAP;
      } else if (needed <= spaceAbove) {
        top = rect.top - MENU_GAP - menuHeight;
      } else {
        // Neither direction fits (very short viewport). Pin inside
        // the viewport and let the menu's own overflow scroll.
        top = Math.max(EDGE_MARGIN, viewportH - menuHeight - EDGE_MARGIN);
      }

      setPosition({ top, left });
    };

    update();
    // Re-measure after the menu actually mounts so the real height
    // replaces the estimate.
    const rafId = requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isOpen, isMobile, anchorEl]);

  // Close on outside click, Escape, shortcut keys.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key.toLowerCase() === "e" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onEdit(template);
        onClose();
      } else if (e.key.toLowerCase() === "d" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onDuplicate(template);
        onClose();
      }
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    window.addEventListener("keydown", handleKey);
    window.addEventListener("mousedown", handleClick);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("mousedown", handleClick);
    };
  }, [isOpen, anchorEl, template, onClose, onEdit, onDuplicate]);

  if (!isOpen) return null;

  const paused = template.status === "paused";
  const ended = template.status === "ended";

  const items: MenuItemDef[] = [
    {
      label: "Edit template",
      icon: Edit2,
      onClick: () => onEdit(template),
      shortcut: "E",
    },
    {
      label: "Duplicate",
      icon: Copy,
      onClick: () => onDuplicate(template),
      shortcut: "D",
    },
    { divider: true },
    {
      label: "Run now",
      icon: Play,
      onClick: () => onRunNow(template),
      disabled: ended,
    },
    {
      label: "Skip next occurrence",
      icon: SkipForward,
      onClick: () => onSkipNext(template),
      disabled: template.nextDate === null,
    },
    {
      label: paused ? "Resume" : "Pause",
      icon: paused ? Play : Pause,
      onClick: () => onTogglePause(template),
      disabled: ended,
    },
    { divider: true },
    {
      label: "End this recurring",
      icon: Square,
      onClick: () => onEnd(template),
      disabled: ended,
    },
    { divider: true },
    {
      label: "Delete template",
      icon: Trash2,
      onClick: () => onDelete(template),
      destructive: true,
    },
  ];

  if (isMobile) {
    return createPortal(
      <>
        <div style={sheetOverlayStyle} onClick={onClose} aria-hidden="true" />
        <div
          ref={menuRef}
          style={sheetStyle}
          role="menu"
          aria-label={`Actions for ${template.name}`}
        >
          <div style={sheetHandleStyle} />
          <div style={sheetTitleStyle}>{template.name}</div>
          {items.map((item, idx) => (
            <MenuItem
              key={idx}
              item={item}
              variant="sheet"
              onClick={() => {
                if (!item.disabled && item.onClick) {
                  item.onClick();
                  onClose();
                }
              }}
            />
          ))}
        </div>
      </>,
      document.body,
    );
  }

  if (!position) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{ ...dropdownStyle, top: position.top, left: position.left }}
      role="menu"
      aria-label={`Actions for ${template.name}`}
    >
      {items.map((item, idx) => (
        <MenuItem
          key={idx}
          item={item}
          variant="dropdown"
          onClick={() => {
            if (!item.disabled && item.onClick) {
              item.onClick();
              onClose();
            }
          }}
        />
      ))}
    </div>,
    document.body,
  );
}

interface MenuItemDef {
  label?: string;
  icon?: typeof Edit2;
  onClick?: () => void;
  shortcut?: string;
  destructive?: boolean;
  disabled?: boolean;
  divider?: boolean;
}

interface MenuItemProps {
  item: MenuItemDef;
  variant: "dropdown" | "sheet";
  onClick: () => void;
}

function MenuItem({ item, variant, onClick }: MenuItemProps) {
  if (item.divider) {
    return <div style={dividerStyle} />;
  }
  const Icon = item.icon;
  const isDestructive = item.destructive;
  const bgHover = isDestructive ? PALETTE.redWash : PALETTE.bgFade;

  const baseStyle: CSSProperties =
    variant === "sheet" ? sheetItemStyle : dropdownItemStyle;

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={item.disabled}
      style={{
        ...baseStyle,
        color: isDestructive ? PALETTE.red : PALETTE.ink,
        opacity: item.disabled ? 0.4 : 1,
        cursor: item.disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!item.disabled) e.currentTarget.style.background = bgHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {Icon && (
        <Icon
          size={variant === "sheet" ? 18 : 15}
          color={isDestructive ? PALETTE.red : PALETTE.ink3}
          strokeWidth={2}
        />
      )}
      <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
      {item.shortcut && variant === "dropdown" && (
        <span style={shortcutStyle}>{item.shortcut}</span>
      )}
    </button>
  );
}

const dropdownStyle: CSSProperties = {
  position: "fixed",
  width: MENU_WIDTH,
  background: "#ffffff",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  boxShadow: "0 12px 32px -4px rgba(14, 26, 26, 0.18)",
  padding: "6px 0",
  zIndex: 2000,
};

const dropdownItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 500,
  background: "transparent",
  border: 0,
  width: "100%",
  fontFamily: "inherit",
  transition: "background 0.1s",
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: PALETTE.lineSoft,
  margin: "4px 0",
};

const shortcutStyle: CSSProperties = {
  marginLeft: "auto",
  fontSize: 11,
  color: PALETTE.ink4,
  fontFamily: "ui-monospace, Menlo, monospace",
};

const sheetOverlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
  zIndex: 1999,
};

const sheetStyle: CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  background: "#ffffff",
  borderRadius: "16px 16px 0 0",
  padding: "6px 0 20px",
  zIndex: 2000,
  boxShadow: "0 -8px 24px -4px rgba(14, 26, 26, 0.14)",
  maxHeight: "80vh",
  overflowY: "auto",
};

const sheetHandleStyle: CSSProperties = {
  width: 36,
  height: 4,
  background: PALETTE.line,
  borderRadius: 2,
  margin: "10px auto 14px",
};

const sheetTitleStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: PALETTE.ink4,
  fontWeight: 700,
  padding: "0 20px 10px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  marginBottom: 6,
};

const sheetItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 20px",
  fontSize: 14,
  fontWeight: 500,
  background: "transparent",
  border: 0,
  width: "100%",
  fontFamily: "inherit",
  textAlign: "left",
  transition: "background 0.1s",
};